'use client';

import React, { createContext, useContext, useReducer } from 'react';
import type { AccordionSize } from '../Accordion'; // Import the size type

interface AccordionState {
  openIndex: number[];
}

type AccordionAction = {
  type: 'TOGGLE';
  payload: {
    index: number;
  };
};

interface AccordionContextType {
  state: AccordionState;
  dispatch: React.Dispatch<AccordionAction>;
  size: AccordionSize; // Add size to context type
}

const AccordionContext = createContext<AccordionContextType | undefined>(
  undefined
);

const accordionReducer = (
  state: AccordionState,
  action: AccordionAction
): AccordionState => {
  switch (action.type) {
    case 'TOGGLE':
      return {
        ...state,
        openIndex: state.openIndex?.includes(action.payload.index)
          ? state.openIndex.filter((index) => index !== action.payload.index)
          : [...(state.openIndex || []), action.payload.index],
      };
  }
};

// Define props for the provider
interface AccordionProviderProps {
  children: React.ReactNode;
  size: AccordionSize; // Accept size prop
  defaultOpen?: boolean; // Accept defaultOpen prop
}

export const AccordionProvider = ({
  children,
  size,
  defaultOpen,
}: AccordionProviderProps) => {
  const [state, dispatch] = useReducer(accordionReducer, {
    openIndex: defaultOpen ? [0] : [],
  });

  const contextValue = React.useMemo(
    () => ({ state, dispatch, size }),
    [state, dispatch, size]
  );

  return (
    <AccordionContext.Provider value={contextValue}>
      {children}
    </AccordionContext.Provider>
  );
};

export const useAccordion = () => {
  const context = useContext(AccordionContext);
  if (!context) {
    throw new Error('useAccordion must be used within an AccordionProvider');
  }
  return context;
};

export const useAccordionItem = (index: number) => {
  const { state, dispatch, size } = useAccordion(); // Get size from context
  return {
    isOpen: state.openIndex?.includes(index) ?? false,
    onToggle: () => dispatch({ type: 'TOGGLE', payload: { index } }),
    size, // Return size
  };
};

export const useAccordionSummary = (index: number) => {
  const { state, dispatch, size } = useAccordion(); // Get size from context
  return {
    isOpen: state.openIndex?.includes(index) ?? false,
    onToggle: () => dispatch({ type: 'TOGGLE', payload: { index } }),
    size, // Return size
  };
};

export const useAccordionDetails = (index: number) => {
  const { state, size } = useAccordion(); // Get size from context
  return {
    isOpen: state.openIndex?.includes(index) ?? false,
    size, // Return size
  };
};
